<template>
  <Transition name="fade">
    <div
      v-if="availablePages.length"
      class="w-full lg:relative group"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <component
        :is="hasDropdown ? 'button' : 'LangRouterLink'"
        class="w-full bg-white p-2 rounded-lg flex gap-2.5 items-center lg:w-max cursor-pointer lg:py-9.5"
        @click.stop="handleAction"
        :to="hasDropdown ? null : { name: availablePages[0] }"
      >
        <span
          :class="[
            'w-8 h-8 rounded-full flex items-center justify-center bg-grey text-[18px] transition-[color_background]',
            {
              'lg:bg-(--main-color) lg:group-hover:bg-(--main-color) lg:text-white':
                active || opened,
            },
          ]"
        >
          <IconSprite :id="section.icon" />
        </span>
        <span class="font-semibold">{{ section.title }}</span>
        <IconSprite
          v-if="hasDropdown"
          id="chevron"
          :class="['ml-auto mr-3 text-[8px] transition-[rotate]', { '-rotate-180': opened }]"
        />
      </component>
      <div
        v-if="hasDropdown"
        :class="[
          'grid grid-rows-[0fr] transition-[grid-template-rows_opacity] w-full lg:absolute opacity-0',
          { 'pt-1 grid-rows-[1fr] lg:pt-0 opacity-100 lg:drop-shadow-menu': opened },
        ]"
      >
        <div
          class="bg-white rounded-lg overflow-hidden w-full lg:bg-(--main-color) lg:rounded-t-none lg:text-white group"
        >
          <LangRouterLink
            v-for="template in availablePages"
            :to="{ name: ROUTES_MAP[template] }"
            @mouseenter="preloadView(template)"
            :key="`mobile-menu-page-${pages[template].id}`"
            class="py-5 block pr-5 pl-13 border-grey border-b last-of-type:border-b-none font-semibold lg:px-2.5 lg:border-b-white/30 lg:last-of-type:border-b-none lg:mx-5 lg:hover:pl-4 lg:hover:pr-0.5 transition-[padding] overflow-hidden"
          >
            {{ pages[template].title }}
          </LangRouterLink>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { IconSprite, LangRouterLink } from "@/components/helpers";
import ROUTES from "@/config/ROUTES.ts";
import {
  useDocumentStore,
  useEventStore,
  useNewsStore,
  usePageStore,
  usePostStore,
} from "@/stores";

export default {
  name: "NavigationSection",
  components: { IconSprite, LangRouterLink },
  setup() {
    return {
      ROUTES_MAP: {
        about: ROUTES.ABOUT,
        careers: ROUTES.CAREERS,
        documents: ROUTES.DOCUMENT.INDEX,
        events: ROUTES.EVENT.INDEX,
        funding: ROUTES.FUNDING,
        home: ROUTES.HOME,
        innovation: ROUTES.INNOVATION,
        news: ROUTES.NEWS.INDEX,
        not_found: ROUTES.NOT_FOUND,
        policies: ROUTES.POLICIES,
        posts: ROUTES.POST.INDEX,
        projects: ROUTES.PROJECTS,
        team: ROUTES.TEAM,
      },
    };
  },
  data() {
    return {
      opened: false as boolean,
    };
  },
  props: {
    pages: {
      type: Object as PropType<Record<template, Page>>,
      required: true,
    },
    section: {
      type: Object as PropType<MenuSection>,
      required: true,
    },
  },
  computed: {
    active() {
      return Array.from(this.section.pages).some(
        (template: template) =>
          this.ROUTES_MAP[template] === (this.$route.name ?? "").toString().split(":").shift(),
      );
    },
    availablePages() {
      return Array.from(this.section.pages).filter((page) => this.pages[page] !== undefined);
    },
    hasDropdown() {
      return this.availablePages.length > 1;
    },
  },
  methods: {
    async handleAction() {
      if (this.hasDropdown) {
        this.opened = !this.opened;
      }
    },
    handleMouseEnter() {
      if (window.innerWidth > 1024) {
        this.opened = true;
      }
    },
    handleMouseLeave() {
      if (window.innerWidth > 1024) {
        this.opened = false;
      }
    },
    preloadView(template: template) {
      usePageStore().get(template);

      switch (template) {
        case "documents":
          return useDocumentStore().all();
        case "events":
          return useEventStore().all();
        case "news":
          return useNewsStore().all();
        case "posts":
          return usePostStore().all();
      }
    },
  },
  watch: {
    $route: {
      handler() {
        this.opened = false;
      },
      deep: true,
    },
  },
};
</script>
